/*
 * Rows for the workflow rail's Connections section: one per credential the workflow will run as, and
 * one for every declared app that has none.
 *
 * The server already resolved `statusCode` and the credential it resolved against; this carries both
 * through, because "which of these is why my workflow is stopped" is the only question the section
 * exists to answer. `not_required` rows are dropped — an app that needs no credential has nothing to
 * say.
 *
 * Icons come from client app metadata rather than the status response: the per-app status read
 * resolves through the MCP app record, whose icon field is not the renderable URL the cards use, so
 * an icon taken from there falls back to an initial. Resolving by id then slug against the same
 * metadata the Connections screen reads is what makes an app look identical in both places.
 *
 * `showAction` folds in the viewer: a workflow run executes as the thread's owner, so a credential
 * another viewer creates from here is one the run will never bind. Rather than mint a credential that
 * silently does not help, a non-owner gets the status and no button.
 *
 * `oneRowPerApp` is the planning phase. There, no step has picked a credential yet, so fanning out every
 * credential the user happens to own for an app answers a question nobody asked and buries the one that
 * matters — three Slack rows, two of them marked `unknown`, when the plan names Slack once. Collapsed, the
 * row names the connection the run would actually bind (the one the server resolved against) and falls
 * back to the app's own name when there is none to name yet.
 *
 * `environmentRefsByAppKey` turns an app whose step names a credential PER ENVIRONMENT into one row
 * per environment instead of one per credential. That is the only honest rendering for it: "what will
 * this run as" has a different answer in each environment, and a flat credential list cannot say which
 * is which. An environment the step does not map reads `not mapped` and carries no Connect action —
 * the credential may well exist; what is missing is the assignment, which only re-authoring the step
 * can supply.
 *
 * Every other row names the environment its credential is tagged with (`environmentName`, '' when
 * untagged), from the company's `environments`: several connections often share one label.
 */
const ACTIONABLE_STATUS_CODES = new Set(['required', 'invalid']);

const buildConnectionRailRows = ({ items, metadataById, metadataBySlug, isThreadOwner, environmentRefsByAppKey = {}, oneRowPerApp = false, environments = [] }) => {
  const environmentNameOf = (connection) => environments.find((environment) => environment?.id === connection?.meta?.environmentId)?.name || '';

  return items.flatMap((item) => {
    if (item?.statusCode === 'not_required') {
      return [];
    }

    const appId = String(item?.appId || '').trim();
    const appSlug = String(item?.appSlug || '').trim();
    const appName = item?.appName || appSlug || appId || 'App';
    const appIcon = metadataById[appId]?.icon || metadataBySlug[appSlug.toLowerCase()]?.icon || item?.appIcon || '';
    const statusCode = String(item?.statusCode || '').trim() || 'unknown';
    /* A credential the status read resolved against is the one a reconnect must reopen. Without it
       there is nothing to reconnect and the action can only create. */
    const resolvedCredentialId = String(item?.credentialId || '').trim();
    const connections = Array.isArray(item?.connections) ? item.connections : [];
    const environmentRefs = environmentRefsByAppKey[appId] || environmentRefsByAppKey[appSlug.toLowerCase()] || null;

    const toRow = ({ key, name, credentialId, rowStatusCode, environmentName = '' }) => ({
      key,
      name,
      environmentName,
      appId,
      appIcon,
      credentialId,
      statusCode: rowStatusCode,
      mode: rowStatusCode === 'invalid' && credentialId ? 'reconnect' : 'create',
      showAction: isThreadOwner && ACTIONABLE_STATUS_CODES.has(rowStatusCode)
    });

    if (environmentRefs) {
      return environmentRefs.map((environmentRef) => {
        /* A ref is a credential id or a connection label, resolved in that order — the same
           precedence resolvePieveMcpActionConnection uses at run time, so the row names the
           connection the run will actually bind. */
        const connection = environmentRef.connectionRef ? connections.find((item) => String(item?.credentialId || '').trim() === environmentRef.connectionRef) || connections.find((item) => String(item?.meta?.label || '').trim() === environmentRef.connectionRef) || null : null;
        const connectionCredentialId = String(connection?.credentialId || '').trim();
        const isResolvedRow = Boolean(resolvedCredentialId) && connectionCredentialId === resolvedCredentialId;

        return toRow({
          key: `${appId || appName}:${environmentRef.environmentId}`,
          name: `${environmentRef.environmentName} → ${connection?.meta?.label || environmentRef.connectionRef || 'not mapped'}`,
          credentialId: connectionCredentialId,
          rowStatusCode: environmentRef.connectionRef ? (isResolvedRow ? statusCode : 'unknown') : 'not_mapped'
        });
      });
    }

    if (oneRowPerApp || connections.length === 0) {
      const resolvedConnection = resolvedCredentialId ? connections.find((connection) => String(connection?.credentialId || '').trim() === resolvedCredentialId) : null;

      return [toRow({ key: appId || appName, name: String(resolvedConnection?.meta?.label || '').trim() || appName, credentialId: resolvedCredentialId, rowStatusCode: statusCode, environmentName: environmentNameOf(resolvedConnection) })];
    }

    return connections.map((connection, index) => {
      const connectionCredentialId = String(connection?.credentialId || '').trim();
      /* The row's own status is the app's status only when the app resolved against this very
         credential — the same limit resolveStepConnectionStatus documents. Any other credential in
         the list is one the server did not judge, so it is shown without a verdict. */
      const isResolvedRow = Boolean(resolvedCredentialId) && connectionCredentialId === resolvedCredentialId;

      return toRow({
        key: `${appId}:${connectionCredentialId || index}`,
        name: String(connection?.meta?.label || connectionCredentialId).trim() || appName,
        credentialId: connectionCredentialId,
        rowStatusCode: isResolvedRow ? statusCode : 'unknown',
        environmentName: environmentNameOf(connection)
      });
    });
  });
};

export default buildConnectionRailRows;
